import {
  BuildProductQuoteResponse,
  CreateQuoteRequest,
} from "@/types/api/quote";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";

import { PlanDurationDays } from "@/types/pages/form";
import { createQuote } from "@/services/quote";
import { showErrorNotification } from "@/utils/notifications/showErrorNotification";
import { readSessionValue, writeSessionValue } from "@/utils/formSessionStorage";

interface QuoteContextData {
  planDurationDays: PlanDurationDays;
  setPlanDurationDays: Dispatch<SetStateAction<PlanDurationDays>>;
  recommendedProductsIds: number[];
  setRecommendedProductsIds: Dispatch<SetStateAction<number[]>>;
  quoteId: number;
  setQuoteId: Dispatch<SetStateAction<number>>;
  quoteItems: BuildProductQuoteResponse[];
  setQuoteItems: Dispatch<SetStateAction<BuildProductQuoteResponse[]>>;

  saveQuote: (setIsLoading: Dispatch<SetStateAction<boolean>>) => Promise<number>;
  removeProductOrGroupFromQuote: (index: number) => void;
  removeProductInsideGroup: (groupIndex: number, productIndex: number) => void;
}

export const QuoteContext = createContext<QuoteContextData>(
  {} as QuoteContextData,
);

export const QuoteProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [planDurationDays, setPlanDurationDays] = useState<PlanDurationDays>(
    () => readSessionValue("planDurationDays", 90),
  );
  const [recommendedProductsIds, setRecommendedProductsIds] = useState<
    number[]
  >(() => readSessionValue("recommendedProductsIds", []));

  const [quoteId, setQuoteId] = useState(() => readSessionValue("quoteId", 0));
  const [quoteItems, setQuoteItems] = useState<BuildProductQuoteResponse[]>(
    () => readSessionValue("quoteItems", []),
  );

  useEffect(() => {
    writeSessionValue("planDurationDays", planDurationDays);
  }, [planDurationDays]);

  useEffect(() => {
    writeSessionValue("recommendedProductsIds", recommendedProductsIds);
  }, [recommendedProductsIds]);

  useEffect(() => {
    writeSessionValue("quoteId", quoteId);
  }, [quoteId]);

  useEffect(() => {
    writeSessionValue("quoteItems", quoteItems);
  }, [quoteItems]);

  const removeProductOrGroupFromQuote = (index: number) => {
    const quoteToRemove = quoteItems[index];

    if (quoteToRemove.type === "product") {
      const productId = quoteToRemove.product!.productId;

      setQuoteItems((prev) =>
        prev.filter((q) => productId !== q.product?.productId),
      );

      const newIds = recommendedProductsIds.filter((id) => id !== productId);

      setRecommendedProductsIds(newIds);
    } else {
      const groupId = quoteToRemove.group!.groupId;

      setQuoteItems((prev) => prev.filter((q) => groupId !== q.group?.groupId));

      const productsIdsToRemove = quoteToRemove.group!.products.map(
        (product) => product.productId,
      );

      const newIds = recommendedProductsIds.filter(
        (id) => !productsIdsToRemove.includes(id),
      );

      setRecommendedProductsIds(newIds);
    }
  };

  const removeProductInsideGroup = (
    groupIndex: number,
    productIndex: number,
  ) => {
    setQuoteItems((prev) =>
      prev.map((q, index) => {
        if (index !== groupIndex || q.type !== "group") {
          return q;
        }

        return {
          ...q,
          group: {
            ...q.group!,
            products: q.group!.products.filter((_, i) => i !== productIndex),
          },
        };
      }),
    );

    const newIds = recommendedProductsIds.filter(
      (id) =>
        id !== quoteItems[groupIndex].group?.products[productIndex].productId,
    );

    setRecommendedProductsIds(newIds);
  };

  const saveQuote = useCallback(
    async (setIsLoading: Dispatch<SetStateAction<boolean>>) => {
      setIsLoading(true);

      try {
        const input: CreateQuoteRequest = {
          planDurationDays: planDurationDays,
          productsAndGroups: [],
        };

        for (const quote of quoteItems) {
          if (quote.type === "product") {
            input.productsAndGroups.push({
              type: "product",
              product: {
                id: quote.product!.productId,
                quantity: quote.product!.quantity,
              },
            });
          } else {
            input.productsAndGroups.push({
              type: "group",
              group: {
                id: quote.group!.groupId,
                products: quote.group!.products.map((product) => ({
                  id: product.productId,
                  quantity: product.quantity,
                })),
              },
            });
          }
        }

        const quote = await createQuote(input);

        setQuoteId(quote.quoteId);

        return quote.quoteId;
      } catch (err) {
        console.log(err);
        showErrorNotification("Erro ao salvar cotação, tente novamente");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [quoteItems, planDurationDays, setQuoteId],
  );

  return (
    <QuoteContext.Provider
      value={{
        planDurationDays,
        setPlanDurationDays,
        quoteId,
        setQuoteId,
        quoteItems,
        setQuoteItems,
        recommendedProductsIds,
        setRecommendedProductsIds,
        saveQuote,
        removeProductOrGroupFromQuote,
        removeProductInsideGroup,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};
